"use client";

import { useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { Item, Coordinates } from "@/lib/types";
import { formatPrice } from "@/lib/utils";
import { MapPinIcon } from "@heroicons/react/24/outline";
import Modal from "@/components/ui/Modal";
import DistanceDisplay from "@/components/common/DistanceDisplay";
import Button from "@/components/ui/Button";
import ImageGallery from "@/components/ui/ImageGallery";
import useSWR from "swr";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import {
  captureItemPreview,
  type ItemPreviewSurface,
} from "@/lib/analytics/captureItemPreview";
import { buildItemBuyHref } from "@/lib/buyer/itemPurchaseGate";

interface ItemPreviewModalProps {
  itemId: string | null;
  isOpen: boolean;
  onClose: () => void;
  initialItem?: Item;
  /** Where the preview was opened — drives Find more + analytics. */
  surface: ItemPreviewSurface;
}

interface ItemData {
  id: string;
  title: string;
  description: string | null;
  defects: string | null;
  price: number;
  quantity: number;
  category: string | null;
  subcategory: string | null;
  location: string | null;
  coordinates: Coordinates | null;
  status: string | null;
  images: Array<{
    id: string;
    url: string;
    position: number | null;
  }>;
}

export default function ItemPreviewModal({
  itemId,
  isOpen,
  onClose,
  initialItem,
  surface,
}: ItemPreviewModalProps) {
  const router = useRouter();
  const { isAuthenticated } = useAuth();
  /** Skip dismiss analytics when closing after a forward CTA. */
  const closedViaCtaRef = useRef(false);
  const openedForIdRef = useRef<string | null>(null);

  const {
    data: itemData,
    error,
    isLoading,
  } = useSWR<ItemData>(
    itemId && !initialItem ? `/api/items/${itemId}` : null,
    async (url: string) => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      const response = await fetch(url, {
        headers: token
          ? {
              Authorization: `Bearer ${token}`,
            }
          : {},
      });

      if (!response.ok) {
        throw new Error("Failed to fetch item");
      }

      return response.json();
    },
  );

  const item = initialItem || itemData;

  useEffect(() => {
    if (!isOpen) {
      openedForIdRef.current = null;
      return;
    }
    if (!itemId || openedForIdRef.current === itemId) return;
    openedForIdRef.current = itemId;
    captureItemPreview({
      cta: "opened",
      surface,
      listingId: itemId,
      category: item?.category ?? null,
      subcategory: item?.subcategory ?? null,
      listingPrice: item?.price ?? null,
    });
  }, [isOpen, itemId, surface, item?.category, item?.subcategory, item?.price]);

  const analyticsBase = {
    surface,
    listingId: item?.id ?? itemId ?? "",
    category: item?.category ?? null,
    subcategory: item?.subcategory ?? null,
    listingPrice: item?.price ?? null,
  };

  const closeAfterCta = () => {
    closedViaCtaRef.current = true;
    onClose();
  };

  const handleDismissClose = () => {
    if (!closedViaCtaRef.current && analyticsBase.listingId) {
      captureItemPreview({
        cta: "dismiss",
        ...analyticsBase,
      });
    }
    closedViaCtaRef.current = false;
    onClose();
  };

  const handleBuy = () => {
    if (!item?.id) return;
    captureItemPreview({ cta: "buy", ...analyticsBase, listingId: item.id });
    closeAfterCta();
    router.push(buildItemBuyHref(item.id));
  };

  const handleFindMore = () => {
    if (analyticsBase.listingId) {
      captureItemPreview({
        cta: "find_more",
        ...analyticsBase,
      });
    }
    closeAfterCta();
    if (surface !== "market") {
      router.push("/market");
    }
  };

  const handleViewFullDetails = () => {
    if (!item?.id) return;
    captureItemPreview({
      cta: "view_details",
      ...analyticsBase,
      listingId: item.id,
    });
    closeAfterCta();
    router.push(`/item/${item.id}`);
  };

  const getGalleryImages = (): Array<{
    id: string;
    item_id: string;
    url: string;
    position: number;
    is_main?: boolean;
  }> => {
    if (!item) return [];

    if (initialItem && Array.isArray(initialItem.images)) {
      const firstImage = initialItem.images[0];
      if (typeof firstImage === "string") {
        return initialItem.images.map((url, index) => ({
          id: `img-${index}`,
          item_id: item.id,
          url,
          position: index,
          is_main: index === 0,
        }));
      }
    }

    if (
      itemData &&
      Array.isArray(itemData.images) &&
      itemData.images.length > 0
    ) {
      const firstImage = itemData.images[0];
      if (
        typeof firstImage === "object" &&
        firstImage !== null &&
        "url" in firstImage
      ) {
        return itemData.images.map((img) => ({
          id: img.id || `img-${img.position ?? 0}`,
          item_id: item.id,
          url: img.url,
          position: img.position ?? 0,
          is_main: img.position === 0,
        }));
      }
    }

    return [];
  };

  const galleryImages = getGalleryImages();
  const buyLabel = isAuthenticated
    ? `Buy · ${formatPrice(item?.price ?? 0)}`
    : "Sign in to buy";
  const showBuy =
    Boolean(item?.id) && (item?.status == null || item?.status === "approved");

  const previewFooter =
    !isLoading || initialItem ? (
      error || !item ? undefined : (
        <div className="flex flex-col gap-3 border-t border-border pt-4">
          {showBuy ? (
            <Button
              onClick={handleBuy}
              variant="primary"
              className="w-full"
              haptic="light"
            >
              {buyLabel}
            </Button>
          ) : null}
          <div className="flex items-center gap-3">
            <Button
              onClick={handleFindMore}
              variant="secondary"
              className="min-w-0 flex-1"
            >
              Find more items
            </Button>
            <button
              type="button"
              onClick={handleViewFullDetails}
              className="min-w-0 flex-1 text-center text-sm text-text-light hover:text-foreground underline-offset-2 hover:underline py-2"
            >
              View full details
            </button>
          </div>
        </div>
      )
    ) : undefined;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleDismissClose}
      title={item?.title || "Item Preview"}
      maxWidth="2xl"
      hideFooter={!previewFooter}
      footer={previewFooter}
      className="max-h-[80dvh] sm:max-h-[80vh]"
    >
      {isLoading && !initialItem ? (
        <div className="space-y-4">
          <div className="animate-pulse">
            <div className="aspect-[4/3] bg-tertiary rounded-lg mb-4" />
            <div className="h-6 bg-tertiary rounded w-3/4 mb-2" />
            <div className="h-4 bg-tertiary rounded w-1/2" />
          </div>
        </div>
      ) : error || !item ? (
        <div className="text-center py-8">
          <p className="text-text-light mb-4">
            Unable to load item details. Please try again.
          </p>
          <Button onClick={handleDismissClose} variant="secondary">
            Close
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          {galleryImages.length > 0 ? (
            <ImageGallery
              images={galleryImages}
              title={item.title}
              onImageError={() => {}}
            />
          ) : (
            <div className="aspect-[4/3] w-full bg-secondary rounded-lg flex items-center justify-center">
              <span className="text-4xl font-bold text-text-light">
                {item.title.charAt(0)}
              </span>
            </div>
          )}

          <div className="flex items-center justify-between">
            <div>
              <div className="text-3xl font-bold text-primary">
                {formatPrice(item.price)}
              </div>
              {item.quantity > 1 && (
                <div className="text-sm text-text-light mt-1">
                  {item.quantity} units available
                </div>
              )}
            </div>
            {item.coordinates && (
              <DistanceDisplay coordinates={item.coordinates} variant="badge" />
            )}
          </div>

          {item.location && (
            <div className="flex items-center text-text-light">
              <MapPinIcon className="h-5 w-5 mr-2 text-primary flex-shrink-0" />
              <span className="text-sm">{item.location}</span>
            </div>
          )}

          {item.description && (
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-2">
                Description
              </h3>
              <p className="text-sm text-text-light line-clamp-4">
                {item.description}
              </p>
            </div>
          )}

          {item.defects && (
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-2">
                Defects
              </h3>
              <p className="text-sm text-text-light line-clamp-2">
                {item.defects}
              </p>
            </div>
          )}

          {item.category && (
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-text-light">
                Category:
              </span>
              <span className="text-xs bg-secondary px-2 py-1 rounded">
                {item.category}
              </span>
              {item.subcategory && (
                <>
                  <span className="text-text-light">•</span>
                  <span className="text-xs bg-secondary px-2 py-1 rounded">
                    {item.subcategory}
                  </span>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
